import { compareAsc, isAfter, isBefore, isEqual, parseISO } from 'date-fns';

/**
 * Normalize any input (ISO string, timestamp, or Date) to a JS Date
 * @param {string|number|Date} input — ISO date string, millisecond timestamp, or Date object
 * @returns {Date} — parsed Date instance
 * @throws {TypeError} — if input is not a supported date type
 */
export const toDate = input => {
  if (typeof input === 'string') {
    return parseISO(input);
  }
  if (typeof input === 'number') {
    return new Date(input);
  }
  if (input instanceof Date) {
    return input;
  }
  throw new TypeError('Unsupported date input');
};

/**
 * Numeric comparator: 1 if a > b, 0 if a == b, -1 if a < b
 * @param {string|number|Date} a — first date input
 * @param {string|number|Date} b — second date input
 * @returns {number} — comparison result: 1 if a is newer than b, 0 if equal, -1 if older
 */
export const numericCompare = (a, b) => {
  // if either input is null or undefined, bail out as false
  if (a == null || b == null) {
    return false;
  }

  return compareAsc(toDate(a), toDate(b));
};

/**
 * Check if date a is after (newer than) date b
 * @param {string|number|Date} a — date to test
 * @param {string|number|Date} b — reference date
 * @returns {boolean} — true if a is newer than b
 */
export const isNewer = (a, b) => {
  // if either input is null or undefined, bail out as false
  if (a == null || b == null) {
    return false;
  }

  return isAfter(toDate(a), toDate(b));
};

/**
 * Check if date a is before (older than) date b
 * @param {string|number|Date} a — date to test
 * @param {string|number|Date} b — reference date
 * @returns {boolean} — true if a is older than b
 */
export const isOlder = (a, b) => {
  // if either input is null or undefined, bail out as false
  if (a == null || b == null) {
    return false;
  }

  return isBefore(toDate(a), toDate(b));
};

/**
 * Check if date a is exactly the same as date b
 * @param {string|number|Date} a — first date input
 * @param {string|number|Date} b — second date input
 * @returns {boolean} — true if both dates represent the exact same moment
 */
export const isSame = (a, b) => {
  // if either input is null or undefined, bail out as false
  if (a == null || b == null) {
    return false;
  }

  return isEqual(toDate(a), toDate(b));
};

/**
 * Check if date a is newer than or the same as date b
 * @param {string|number|Date | null | undefined} a — date to test
 * @param {string|number|Date | null | undefined} b — reference date
 * @returns {boolean} — true if a is newer than or equal to b
 */
export const isNewerOrSame = (a, b) => {
  // if either input is null or undefined, bail out as false
  if (a == null || b == null) {
    return false;
  }

  const dateA = toDate(a);
  const dateB = toDate(b);
  return !isBefore(dateA, dateB);
};
